// HELLO, WELCOME, CLICK and REJECT, ported from internal/wire/clientmsg.go.
//
// The handshake, its answer, one click, and the server's last word before a close.
//
//	HELLO    u8 tokenLen | tokenLen x u8    (tick is unused and must be 0)
//	WELCOME  16 x u8 session | u16 country | u8 locked | u64 connId
//	CLICK    16 x u8 session | u32 target | u8 kind | u32 seq
//	REJECT   u16 code | u16 reasonLen | reasonLen x u8
//
// HELLO carries the raw token exactly once, because the browser WebSocket
// constructor cannot set a request header (§4). Everything after the handshake
// names the SESSION, not the token: every frame carrying the token is another
// place it can leak.

import { Reader, Writer, encodeUtf8 } from './bytes';
import { ClassClick, ClassHello, ClassReject, ClassWelcome } from './classes';
import { MaxClickKind, validPixel, type ClickKind, type CountryID, type PixelID, type TickNo } from './core';
import { errBadBool, errClickKind, errClickTarget, errRejectText, errTokenLen } from './errors';

/** 16 bytes of randomness: not guessable, and short enough to sit in every CLICK frame. */
export const SessionIDLen = 16;
export type SessionID = Uint8Array;

/** identity.Token is 16 bytes as 22 characters of unpadded base64url; the cap is generous. */
export const MaxTokenLen = 64;
export const MaxRejectText = 256;

// --- HELLO ----------------------------------------------------------------

export function encodeHello(token: string): Uint8Array {
  const body = encodeUtf8(token);
  if (body.byteLength === 0 || body.byteLength > MaxTokenLen) throw errTokenLen();
  // The header tick is unused and must be zero: the client has applied nothing yet.
  return new Writer(9 + 1 + body.byteLength)
    .header(ClassHello, 0)
    .u8(body.byteLength)
    .bytes(body)
    .finish();
}

/** The client never receives one; the decoder exists to prove the encoder against. */
export function decodeHello(b: Uint8Array): { token: string } {
  const r = new Reader(b);
  r.header(ClassHello);
  const n = r.u8();
  if (n === 0 || n > MaxTokenLen) throw errTokenLen();
  const tok = r.text(n);
  r.done();
  return { token: tok };
}

// --- WELCOME --------------------------------------------------------------

export interface Welcome {
  tick: TickNo;
  session: SessionID;
  /**
   * The country the token is locked to (§4).
   *
   * Neutral with `locked` false means geo-IP did not resolve this address and the
   * next visit that does resolve takes the lock. The client must render that as
   * "no country yet" rather than as membership of Neutral, which is territory,
   * not a side.
   */
  country: CountryID;
  locked: boolean;
  /** This connection's server-side id, useful only for correlating logs. */
  connID: number;
}

export function decodeWelcome(b: Uint8Array): Welcome {
  const r = new Reader(b);
  const tick = r.header(ClassWelcome);
  const session = r.copy(SessionIDLen);
  const country = r.u16();
  const locked = r.u8();
  const connID = r.u64();
  r.done();
  if (locked > 1) throw errBadBool();
  return { tick, session, country, locked: locked === 1, connID };
}

/** Server-side; here so a test can build a frame the decoder is run against. */
export function encodeWelcome(w: Welcome): Uint8Array {
  return new Writer(9 + SessionIDLen + 2 + 1 + 8)
    .header(ClassWelcome, w.tick)
    .bytes(w.session)
    .u16(w.country)
    .u8(w.locked ? 1 : 0)
    .u64(w.connID)
    .finish();
}

// --- CLICK ----------------------------------------------------------------

export interface Click {
  /** The last tick the client applied, exactly as on SUBSCRIBE. */
  tick: TickNo;
  session: SessionID;
  target: PixelID;
  kind: ClickKind;
  /** The client's own counter, echoed on the SELF verdict so an optimistic client can match a correction to the square it drew. */
  seq: number;
}

export function encodeClick(c: Click): Uint8Array {
  if (c.kind > MaxClickKind) throw errClickKind();
  if (!validPixel(c.target)) throw errClickTarget();
  return new Writer(9 + SessionIDLen + 4 + 1 + 4)
    .header(ClassClick, c.tick)
    .bytes(c.session)
    .u32(c.target)
    .u8(c.kind)
    .u32(c.seq)
    .finish();
}

/** The client never receives one; the decoder exists to prove the encoder against. */
export function decodeClick(b: Uint8Array): Click {
  const r = new Reader(b);
  const tick = r.header(ClassClick);
  const session = r.copy(SessionIDLen);
  const target = r.u32();
  const kind = r.u8();
  const seq = r.u32();
  r.done();
  if (kind > MaxClickKind) throw errClickKind();
  if (!validPixel(target)) throw errClickTarget();
  return { tick, session, target, kind: kind as ClickKind, seq };
}

// --- REJECT ---------------------------------------------------------------

/** The handshake token was malformed, zero, or refused. */
export const RejectBadToken = 1;
/** A message arrived that the server cannot parse, or the handshake did not come first. */
export const RejectProtocol = 2;
/** The connection sent faster than the inbound burst allows. A flood, not play. */
export const RejectRateLimit = 3;
/** Rung 4 of the §3 ladder. The reconnect hint: come back, a fresh connection starts at rung 0. */
export const RejectBackpressure = 4;
export const RejectShutdown = 5;
export const RejectTooBig = 6;
/** Nothing arrived within the read timeout. An empty SUBSCRIBE is the client heartbeat. */
export const RejectIdle = 7;

export type RejectCode = number;

const REJECT_NAMES: Record<number, string> = {
  [RejectBadToken]: 'bad-token',
  [RejectProtocol]: 'protocol',
  [RejectRateLimit]: 'rate-limit',
  [RejectBackpressure]: 'backpressure',
  [RejectShutdown]: 'shutdown',
  [RejectTooBig]: 'message-too-big',
  [RejectIdle]: 'idle',
};

export function rejectName(c: number): string {
  return REJECT_NAMES[c] ?? 'unknown';
}

/**
 * Whether the client should come straight back. Rung 4 of the ladder, a shutdown
 * and an idle timeout all mean "try again"; a bad token or a protocol violation
 * mean "you are broken, stop".
 */
export function rejectReconnect(c: number): boolean {
  return c === RejectBackpressure || c === RejectShutdown || c === RejectIdle;
}

export interface Reject {
  tick: TickNo;
  code: RejectCode;
  reason: string;
}

export function decodeReject(b: Uint8Array): Reject {
  const r = new Reader(b);
  const tick = r.header(ClassReject);
  const code = r.u16();
  const n = r.u16();
  if (n > MaxRejectText) throw errRejectText();
  const reason = r.text(n);
  r.done();
  return { tick, code, reason };
}

/** Server-side; here so a test can build a frame the decoder is run against. */
export function encodeReject(rj: Reject): Uint8Array {
  const body = encodeUtf8(rj.reason);
  if (body.byteLength > MaxRejectText) throw errRejectText();
  return new Writer(9 + 4 + body.byteLength)
    .header(ClassReject, rj.tick)
    .u16(rj.code)
    .u16(body.byteLength)
    .bytes(body)
    .finish();
}
